import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Building2, Pencil, Check, X, Loader2 } from 'lucide-react';
import InfoRow from './InfoRow';

const BasicInfoSection = ({ shop, canEdit, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedInfo, setEditedInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleStartEdit = () => {
    setEditedInfo({
      shopName: shop.shopName || '',
      address: shop.address || '',
      city: shop.city || '',
      state: shop.state || '',
      shopPhoneNumber: shop.shopPhoneNumber || '',
      contactPerson: shop.contactPerson || '',
      contactNumber: shop.contactNumber || '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedInfo(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Note: There's no API endpoint for updating basic shop info
      onUpdate(editedInfo);
      setIsEditing(false);
      setEditedInfo(null);
      alert('Note: Basic info changes are saved locally. Backend API endpoint for updating shop info is not yet available.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setEditedInfo(prev => ({ ...prev, [field]: value }));
  };

  return (
    <AccordionItem value="basic" className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
      <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[#334155]/30" data-testid="basic-info-trigger">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#60A5FA]" />
          </div>
          <span className="text-lg font-semibold text-[#E2E8F0]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Basic Info
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-6 pb-4">
        {canEdit && (
          <div className="flex justify-end mb-4">
            {isEditing ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
                  data-testid="cancel-edit-basic"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  data-testid="save-basic-info"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Save Changes
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handleStartEdit}
                className="border-[#2563EB]/30 text-[#60A5FA] hover:bg-[#2563EB]/20"
                data-testid="edit-basic-info"
              >
                <Pencil className="w-4 h-4 mr-1" />
                Edit Details
              </Button>
            )}
          </div>
        )}

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Shop Name</Label>
                <Input
                  value={editedInfo.shopName}
                  onChange={(e) => handleChange('shopName', e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Address</Label>
                <Input
                  value={editedInfo.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">City</Label>
                <Input
                  value={editedInfo.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">State</Label>
                <Input
                  value={editedInfo.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Phone Number</Label>
                <Input
                  value={editedInfo.shopPhoneNumber}
                  onChange={(e) => handleChange('shopPhoneNumber', e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Contact Person</Label>
                <Input
                  value={editedInfo.contactPerson}
                  onChange={(e) => handleChange('contactPerson', e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] text-sm">Contact Number</Label>
                <Input
                  value={editedInfo.contactNumber}
                  onChange={(e) => handleChange('contactNumber', e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] h-10"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <InfoRow label="Shop Name" value={shop.shopName} />
            <InfoRow label="Shop ID" value={shop.shopId} />
            <InfoRow label="Address" value={shop.address} />
            <InfoRow label="City" value={shop.city} />
            <InfoRow label="State" value={shop.state} />
            <InfoRow label="Phone Number" value={shop.shopPhoneNumber} />
            <InfoRow label="Contact Person" value={shop.contactPerson} />
            <InfoRow label="Contact Number" value={shop.contactNumber} />
            <InfoRow label="Website" value={shop.websiteLink} isLink />
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

export default BasicInfoSection;
